/**
 * PinPad Component - HAL 3000
 * Version 3.2.0 - 2026-03-13
 * Écran d'authentification avec sons
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth, AUTH_LEVELS } from './AuthContext';
import { Lock, User, Shield, Delete } from 'lucide-react';

// Audio Context pour les sons
let audioCtx = null;
const getAudioContext = () => {
  if (!audioCtx) {
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      return null;
    }
  }
  return audioCtx;
};

// Son de touche
const playKeySound = (freq = 600) => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.1, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.08);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.08);
  } catch (e) {}
};

// Son de succès
const playSuccessSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    [523, 659, 784, 1047].forEach((freq, i) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.12, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.15);
      }, i * 80);
    });
  } catch (e) {}
};

// Son d'erreur
const playErrorSound = () => {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    [0, 150].forEach((delay) => {
      setTimeout(() => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = 200;
        osc.type = 'square';
        gain.gain.setValueAtTime(0.12, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.1);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.1);
      }, delay);
    });
  } catch (e) {}
};

const PinPad = ({ onAuthenticated }) => {
  const { login, loginAsGuest } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState(false);
  const [accessLevel, setAccessLevel] = useState(null);

  const handleDigit = useCallback((digit) => {
    if (code.length < 6 && !success) {
      playKeySound(500 + parseInt(digit) * 50);
      setCode(prev => prev + digit);
      setError(false);
    }
  }, [code, success]);

  const handleDelete = useCallback(() => {
    playKeySound(300);
    setCode(prev => prev.slice(0, -1));
    setError(false);
  }, []);

  const handleClear = useCallback(() => {
    playKeySound(250);
    setCode('');
    setError(false);
  }, []);

  const handleSubmit = useCallback(() => {
    const result = login(code);
    if (result.success) {
      playSuccessSound();
      setSuccess(true);
      setAccessLevel(AUTH_LEVELS.ADMIN);
      setTimeout(() => onAuthenticated(AUTH_LEVELS.ADMIN), 1200);
    } else {
      playErrorSound();
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setCode('');
    }
  }, [code, login, onAuthenticated]);

  const handleGuestAccess = useCallback(() => {
    playSuccessSound();
    loginAsGuest();
    setSuccess(true);
    setAccessLevel(AUTH_LEVELS.GUEST);
    setTimeout(() => onAuthenticated(AUTH_LEVELS.GUEST), 1200);
  }, [loginAsGuest, onAuthenticated]);

  // Auto-submit when 6 digits
  useEffect(() => {
    if (code.length === 6) handleSubmit();
  }, [code, handleSubmit]);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (success) return;
      if (e.key >= '0' && e.key <= '9') handleDigit(e.key);
      else if (e.key === 'Backspace') handleDelete();
      else if (e.key === 'Escape') handleClear();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [code, success, handleDigit, handleDelete, handleClear]);

  const digits = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'C', '0', '⌫'];

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'radial-gradient(circle, #00f0ff 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} />

      <div className={`relative z-10 ${shake ? 'animate-shake' : ''}`} style={{ width: '320px' }}>
        {/* HAL Eye */}
        <div className="flex justify-center mb-4">
          <div className="w-20 h-20 rounded-full overflow-hidden" style={{
            boxShadow: success ? '0 0 40px #00ff9f' : '0 0 40px #ff0000'
          }}>
            <img src="/hal-eye.gif" alt="HAL 3000" className="w-full h-full object-cover" />
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold font-['Orbitron'] tracking-wider mb-1"
              style={{ color: '#00f0ff', textShadow: '0 0 20px #00f0ff' }}>
            HAL 3000
          </h1>
          <p className="text-cyan-400 font-mono text-xs">SYSTÈME DOMOTIQUE SÉCURISÉ</p>
          <p className="text-gray-600 font-mono text-xs">hal3000.fr</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="text-center mb-4 animate-pulse">
            <div className={`text-lg font-bold font-mono ${accessLevel === AUTH_LEVELS.ADMIN ? 'text-green-400' : 'text-cyan-400'}`}>
              {accessLevel === AUTH_LEVELS.ADMIN ? (
                <><Shield className="inline w-5 h-5 mr-2" />ACCÈS ADMIN</>
              ) : (
                <><User className="inline w-5 h-5 mr-2" />MODE INVITÉ</>
              )}
            </div>
          </div>
        )}

        {!success && (
          <>
            {/* PIN Display */}
            <div className="flex justify-center gap-2 mb-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div key={i}
                  className={`w-10 h-12 rounded flex items-center justify-center text-xl font-mono border-2 ${
                    error ? 'border-red-500 bg-red-900/30' : 
                    code[i] ? 'border-cyan-400 bg-cyan-900/30' : 'border-gray-700 bg-gray-900/30'
                  }`}>
                  {code[i] ? <span className="text-cyan-400">●</span> : <span className="text-gray-700">_</span>}
                </div>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div className="text-center mb-3 text-red-400 font-mono text-xs animate-pulse">
                CODE INCORRECT
              </div>
            )}

            {/* Keypad */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 mb-4">
              <div className="grid grid-cols-3 gap-2">
                {digits.map((digit, i) => (
                  <button key={i}
                    onClick={() => {
                      if (digit === 'C') handleClear();
                      else if (digit === '⌫') handleDelete();
                      else handleDigit(digit);
                    }}
                    className={`h-12 rounded font-bold text-lg font-mono transition-all active:scale-95 ${
                      digit === 'C' ? 'bg-orange-900/50 text-orange-400 border border-orange-600' :
                      digit === '⌫' ? 'bg-red-900/50 text-red-400 border border-red-600' :
                      'bg-gray-800 text-cyan-400 border border-gray-600 hover:bg-gray-700'
                    }`}>
                    {digit === '⌫' ? <Delete className="w-5 h-5 mx-auto" /> : digit}
                  </button>
                ))}
              </div>
            </div>

            {/* Guest Access */}
            <button onClick={handleGuestAccess}
              className="w-full py-2 rounded font-mono text-sm flex items-center justify-center gap-2 bg-gray-900 border border-cyan-700 text-cyan-400 hover:bg-gray-800">
              <User className="w-4 h-4" />
              ACCÈS INVITÉ
            </button>

            <p className="text-center text-gray-600 text-xs font-mono mt-2">
              <Lock className="inline w-3 h-3 mr-1" />
              Code admin requis pour accès complet
            </p>
            
            <p className="text-center text-cyan-600 text-xs font-mono mt-2">v3.2.0</p>
          </>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
};

export default PinPad;
